import { FC } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import SidePanel, {
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';

const ButtonsHolder = styled.div`
  flex-direction: column;
  display: flex;
  gap: 16px;
`;

const StyledButton = styled(Button)`
  grid-column: span 2;
`;

const SettingsButton = styled(Button)`
  width: 100%;
  justify-content: flex-start;
  gap: 8px;
  padding: 0 16px;
  height: 46px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray};
  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
  }
  &:active {
    border-color: ${({ theme }) => theme.colors.gray};
  }
`;

interface Props {
  onOpenVacationSettings: () => void;
  onAddVacationRequest: () => void;
  onAddVacationDays: () => void;
  onClose: () => void;
}

const FreelancerVacationMoreModal: FC<Props> = ({
  onOpenVacationSettings,
  onAddVacationRequest,
  onAddVacationDays,
  onClose,
}) => {
  return (
    <SidePanel heading={`More actions`} onClose={onClose}>
      <SidePanelContent>
        <ButtonsHolder>
          <SettingsButton
            color="secondary"
            onClick={() => {
              onAddVacationDays();
              onClose();
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.86328 10.1003H13.3229"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.0938 6.88965V13.3111"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.0954 18.5281C14.7779 18.5281 18.5737 14.7547 18.5737 10.1C18.5737 5.44526 14.7779 1.67188 10.0954 1.67188C5.41303 1.67188 1.61719 5.44526 1.61719 10.1C1.61719 14.7547 5.41303 18.5281 10.0954 18.5281Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Add extra days
          </SettingsButton>
          <SettingsButton
            color="secondary"
            onClick={() => {
              onAddVacationRequest();
              onClose();
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.86328 10.1003H13.3229"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.0938 6.88965V13.3111"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.0954 18.5281C14.7779 18.5281 18.5737 14.7547 18.5737 10.1C18.5737 5.44526 14.7779 1.67188 10.0954 1.67188C5.41303 1.67188 1.61719 5.44526 1.61719 10.1C1.61719 14.7547 5.41303 18.5281 10.0954 18.5281Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Create Vacation Request
          </SettingsButton>
          <SettingsButton
            color="secondary"
            onClick={() => {
              onOpenVacationSettings();
              onClose();
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 15C12.7614 15 15 12.7614 15 10C15 7.23858 12.7614 5 10 5C7.23858 5 5 7.23858 5 10C5 12.7614 7.23858 15 10 15Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M17.8571 9.28566L18.7774 7.90528C18.8374 7.81635 18.877 7.71531 18.8935 7.60933C18.9101 7.50335 18.903 7.39503 18.873 7.29206C18.6994 6.72037 18.4706 6.16692 18.1898 5.63954C18.1384 5.54566 18.0668 5.46429 17.9803 5.40124C17.8938 5.3382 17.7944 5.29503 17.6893 5.27482L16.0609 4.94912L15.0508 3.93898L14.7251 2.31053C14.7049 2.2054 14.6617 2.10603 14.5987 2.01951C14.5356 1.93299 14.4542 1.86146 14.3603 1.81003C13.8329 1.52929 13.2795 1.30051 12.7078 1.12692C12.6048 1.09687 12.4965 1.08985 12.3905 1.10638C12.2845 1.1229 12.1835 1.16255 12.0945 1.22252L10.7142 2.14272H9.28567L7.90537 1.22252C7.81644 1.16255 7.7154 1.1229 7.60942 1.10638C7.50344 1.08986 7.39512 1.09687 7.29216 1.12692C6.72046 1.30051 6.167 1.52929 5.63959 1.81002C5.5457 1.86146 5.46432 1.93299 5.40126 2.01951C5.33821 2.10604 5.29504 2.20541 5.27484 2.31055L4.94921 3.9389L3.93899 4.94911L2.31063 5.27474C2.2055 5.29494 2.10613 5.33811 2.01961 5.40116C1.93309 5.4642 1.86156 5.54558 1.81013 5.63946C1.52934 6.16688 1.30053 6.72035 1.12691 7.29207C1.09686 7.39503 1.08986 7.50335 1.10638 7.60932C1.12291 7.7153 1.16256 7.81634 1.22253 7.90527L2.14273 9.28558V10.7141L1.22252 12.0945C1.16256 12.1834 1.12291 12.2844 1.10639 12.3904C1.08986 12.4964 1.09688 12.6047 1.12692 12.7077C1.30049 13.2794 1.52927 13.8329 1.81003 14.3603C1.86146 14.4542 1.93299 14.5355 2.01951 14.5986C2.10603 14.6616 2.2054 14.7048 2.31053 14.725L3.93899 15.0507L4.94912 16.0608L5.27476 17.6893C5.29496 17.7944 5.33812 17.8938 5.40117 17.9803C5.46422 18.0668 5.54559 18.1384 5.63947 18.1898C6.16686 18.4706 6.72031 18.6994 7.29201 18.8729C7.39497 18.903 7.50328 18.91 7.60925 18.8934C7.71522 18.8769 7.81625 18.8373 7.90518 18.7773L9.28558 17.8571H10.7142L12.0945 18.7774C12.1835 18.8373 12.2845 18.877 12.3905 18.8935C12.4965 18.91 12.6048 18.903 12.7078 18.873C13.2794 18.6994 13.8329 18.4706 14.3603 18.1899C14.4542 18.1384 14.5355 18.0669 14.5986 17.9804C14.6616 17.8939 14.7048 17.7945 14.725 17.6894L15.0507 16.0609L16.0608 15.0507L17.6893 14.7251C17.7944 14.7049 17.8938 14.6617 17.9803 14.5986C18.0668 14.5356 18.1384 14.4542 18.1898 14.3603C18.4706 13.833 18.6994 13.2795 18.873 12.7078C18.903 12.6049 18.9101 12.4966 18.8935 12.3906C18.877 12.2846 18.8373 12.1836 18.7774 12.0946L17.8571 10.7142V9.28566Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Vacation Settings
          </SettingsButton>
        </ButtonsHolder>
      </SidePanelContent>
      <SidePanelFooter $actions>
        <StyledButton color="secondary" onClick={onClose} type="button">
          Close
        </StyledButton>
      </SidePanelFooter>
    </SidePanel>
  );
};

export default FreelancerVacationMoreModal;
